import React, { useState, useEffect, useContext, useCallback } from 'react';
import axiosInstance from '../axiosConfig';
import { buildMediaUrl } from '../apiConfig';
import { DragDropContext, Droppable, Draggable } from '@hello-pangea/dnd';
import { AuthContext } from '../AuthContext';
import { clearComponentModalDataCache, getComponentModalData } from '../utils/componentModalDataCache';
import { Form, Button, Table, Modal, OverlayTrigger, Tooltip } from 'react-bootstrap';
import Select, { components } from 'react-select';
import StorageCategoryModal from './StorageCategoryModal';
import { FaPlus } from 'react-icons/fa';

function ComponentCloneConstructorPage({ onSaveSuccess, componentCloneUpdated }) {
    const { authTokens, setProjects, selectedServiceCenter } = useContext(AuthContext);
    const [projects, setLocalProjects] = useState([]);
    const [storages, setStorages] = useState([]);
    const [compatibilities, setCompatibilities] = useState([]);
    const [selectedProject, setSelectedProject] = useState('');
    const [selectedCompatibility, setSelectedCompatibility] = useState('');
    const [availableComponents, setAvailableComponents] = useState([]);
    const [selectedComponents, setSelectedComponents] = useState([]);
    // const [loading, setLoading] = useState(false); // Удалено, так как не используется
    const [showModal, setShowModal] = useState(false);
    const [modalImage, setModalImage] = useState('');
    const [showStorageCategoryModal, setShowStorageCategoryModal] = useState(false);

    // Обертывание функций в useCallback для стабильности ссылок
    const fetchProjects = useCallback(() => {
        axiosInstance.get('/api/projects/unique/', {
            headers: { 'Authorization': `Bearer ${authTokens.access}` }
        })
            .then(response => setLocalProjects(response.data.projects || []))
            .catch(error => console.error('Ошибка при загрузке проектов:', error));
    }, [authTokens.access]);

    const fetchFilteredData = useCallback(() => {
        getComponentModalData({
            authToken: authTokens.access,
            serviceCenterId: selectedServiceCenter?.id,
        })
            .then(responseData => {
                setStorages((responseData.storages || []).map(storage => ({
                    value: storage.id,
                    label: storage.name
                })));
            })
            .catch(error => console.error('Ошибка при загрузке данных для селекта:', error));
    }, [authTokens.access, selectedServiceCenter?.id]);

    useEffect(() => {
        if (selectedServiceCenter?.id) {
            fetchProjects();
            fetchFilteredData();
        }
    }, [selectedServiceCenter, fetchProjects, fetchFilteredData]);

    const fetchProjectsclone = useCallback(async () => {
        try {
            console.log('Обновление проектов через контекст');
            const response = await axiosInstance.get(`/api/projects/?service_center=${selectedServiceCenter.id}`, {
                headers: {
                    'Authorization': `Bearer ${authTokens.access}`,
                },
            });
            if (response.status === 200) {
                setProjects(response.data.projects || []); // Обновляем проекты в контексте
            }
        } catch (error) {
            console.error('Ошибка при загрузке проектов:', error);
        }
    }, [authTokens.access, selectedServiceCenter.id, setProjects]);

    const fetchCompatibilities = useCallback((projectId) => {
        axiosInstance.get(`/api/compatibilities/clone/${projectId}/`, {
            headers: { 'Authorization': `Bearer ${authTokens.access}` }
        })
            .then(response => setCompatibilities(response.data.compatibilities || []))
            .catch(error => console.error('Ошибка при загрузке совместимостей:', error));
    }, [authTokens.access]);

    const fetchComponents = useCallback((projectId, compatibilityId) => {
        // setLoading(true); // Удалено, так как не используется
        axiosInstance.get(`/api/components/clone/${projectId}/${compatibilityId}/`, {
            headers: { 'Authorization': `Bearer ${authTokens.access}` }
        })
            .then(response => {
                setAvailableComponents(response.data.components);
            })
            .catch(error => console.error('Ошибка при загрузке компонентов:', error))
            .finally(() => {
                // setLoading(false); // Удалено, так как не используется
            });
    }, [authTokens.access]);

    const handleProjectChange = (selectedOption) => {
        const projectId = selectedOption ? selectedOption.value : '';
        setSelectedProject(projectId);
        if (projectId) {
            fetchCompatibilities(projectId);
        } else {
            setCompatibilities([]);
            setSelectedCompatibility('');
            setAvailableComponents([]);
        }
    };

    const handleCompatibilityChange = (selectedOption) => {
        const compatibilityId = selectedOption ? selectedOption.value : '';
        setSelectedCompatibility(compatibilityId);
        if (selectedProject && compatibilityId) {
            fetchComponents(selectedProject, compatibilityId);
        } else {
            setAvailableComponents([]);
        }
    };

    const handleDragEnd = (result) => {
        const { source, destination } = result;
        if (!destination) return;

        if (source.droppableId === destination.droppableId && source.index === destination.index) return;

        if (source.droppableId === 'availableComponents' && destination.droppableId === 'selectedComponents') {
            const movedComponent = availableComponents[source.index];
            setSelectedComponents([...selectedComponents, { ...movedComponent }]);
            setAvailableComponents(availableComponents.filter((_, idx) => idx !== source.index));
        }

        if (source.droppableId === 'selectedComponents' && destination.droppableId === 'availableComponents') {
            const movedComponent = selectedComponents[source.index];
            setAvailableComponents([...availableComponents, movedComponent]);
            setSelectedComponents(selectedComponents.filter((_, idx) => idx !== source.index));
        }
    };

    const handleShowModal = (imageUrl) => {
        setModalImage(imageUrl);
        setShowModal(true);
    };

    const handleSaveClonedComponents = useCallback(() => {
        if (selectedComponents.length === 0) {
            window.Toastify({
                text: "Нет выбранных компонентов для создания.",
                duration: 3000,
                gravity: "top",
                position: "right",
                backgroundColor: "#ff0000"
            }).showToast();
            return;
        }

        for (let i = 0; i < selectedComponents.length; i++) {
            if (!selectedComponents[i].storage_location) {
                window.Toastify({
                    text: `Место хранения не выбрано для компонента ${selectedComponents[i].name}.`,
                    duration: 3000,
                    gravity: "top",
                    position: "right",
                    backgroundColor: "#ff0000"
                }).showToast();
                return;
            }
        }

        const componentsData = selectedComponents.map((component) => {
            let decodedPhoto = component.photo;
            if (decodedPhoto) {
                decodedPhoto = decodeURIComponent(decodedPhoto);
            }

            return {
                name: component.name,
                batch_number: component.batch_number,
                description: component.description || '',
                photo: decodedPhoto,
                project: component.project,
                compatibility: component.compatibility,
                storage_location: component.storage_location
            };
        });

        axiosInstance.post('/api/create_component/', {
            components: componentsData
        }, {
            headers: { 
                'Authorization': `Bearer ${authTokens.access}`,
                'Content-Type': 'application/json'
            }
        })
        .then(response => {
            if (response.data.success) {
                clearComponentModalDataCache(selectedServiceCenter?.id);
                fetchProjectsclone();
                onSaveSuccess();
            } else {
                window.Toastify({
                    text: response.data.errors || "Ошибка при создании компонентов.",
                    duration: 3000,
                    gravity: "top",
                    position: "right",
                    backgroundColor: "#ff0000"
                }).showToast();
            }
        })
        .catch(error => {
            console.error('Ошибка при создании компонентов:', error);
            window.Toastify({
                text: "Ошибка при создании компонентов.",
                duration: 3000,
                gravity: "top",
                position: "right",
                backgroundColor: "#ff0000"
            }).showToast();
        });
    }, [selectedComponents, authTokens.access, fetchProjectsclone, onSaveSuccess, selectedServiceCenter?.id]);

    // Кастомизация меню с плюсиком
    const CustomMenuList = (props) => (
        <components.MenuList {...props}>
            <div
                style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    padding: '8px',
                    cursor: 'pointer',
                    backgroundColor: '#f5f5f5',
                    color: 'black' // Чёрный текст для ссылки
                }}
                onClick={() => setShowStorageCategoryModal(true)}
            >
                <FaPlus /> 
            </div>
            {props.children}  {/* Основной список элементов */}
        </components.MenuList>
    );

    const selectStyles = {
        container: (provided) => ({
            ...provided,
            width: '100%',
        }),
        control: (provided) => ({
            ...provided,
            minHeight: '46px',
            padding: 0,
            borderRadius: '14px',
            display: 'flex',
            alignItems: 'center',
            borderColor: 'var(--warehouse-border)',
            backgroundColor: 'rgba(255, 255, 255, 0.88)',
            boxShadow: 'inset 0 1px 0 rgba(255, 255, 255, 0.78), 0 4px 14px rgba(15, 23, 42, 0.04)',
        }),
        menu: (provided) => ({
            ...provided,
            zIndex: 9999,
            borderRadius: '16px',
            overflow: 'hidden',
            boxShadow: '0 18px 36px rgba(15, 23, 42, 0.12)',
        }),
        singleValue: (provided) => ({
            ...provided,
            color: 'var(--warehouse-text-strong)',
        }),
        valueContainer: (provided) => ({
            ...provided,
            minHeight: '44px',
            padding: '0 14px',
        }),
        input: (provided) => ({
            ...provided,
            margin: 0,
            padding: 0,
            color: 'var(--warehouse-text-strong)',
        }),
        placeholder: (provided) => ({
            ...provided,
            color: 'var(--warehouse-text-muted)',
        }),
        dropdownIndicator: (provided) => ({
            ...provided,
            color: 'rgba(100, 116, 139, 0.8)',
            padding: '0 14px',
        }),
        indicatorSeparator: () => ({
            display: 'none',
        }),
        option: (provided, state) => ({
            ...provided,
            color: 'var(--warehouse-text-strong)',
            backgroundColor: state.isFocused ? 'rgba(204, 251, 241, 0.58)' : '#fff',
        }),
    };

    const renderCellWithTooltip = (text) => (
        <OverlayTrigger
            placement="top"
            overlay={<Tooltip>{text}</Tooltip>}
        >
            <span className="text-truncate" style={{ maxWidth: '150px', display: 'inline-block', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                {text}
            </span>
        </OverlayTrigger>
    );

    return (
        <div className="component-clone-constructor container">
            <h3>Создать компонент на основе существующего</h3>
            <div className="row">
                <div className="col-md-6">
                    <Form.Group controlId="projectSelect" className="mb-3">
                        <Form.Label>Выберите проект</Form.Label>
                        <Select
                            value={projects.find(project => project.value === selectedProject)}
                            options={projects.map(project => ({
                                value: project.id,
                                label: project.name
                            }))}
                            onChange={handleProjectChange}
                            styles={selectStyles}
                            classNamePrefix="warehouse-select"
                            isSearchable
                            isClearable
                        />
                    </Form.Group>
                </div>
                <div className="col-md-6">
                    <Form.Group controlId="compatibilitySelect" className="mb-3">
                        <Form.Label>Выберите совместимость</Form.Label>
                        <Select
                            value={compatibilities.find(compat => compat.value === selectedCompatibility)}
                            options={compatibilities.map(compat => ({
                                value: compat.id,
                                label: compat.name
                            }))}
                            onChange={handleCompatibilityChange}
                            styles={selectStyles}
                            classNamePrefix="warehouse-select"
                            isSearchable
                            isClearable
                        />
                    </Form.Group>
                </div>
            </div>

            <DragDropContext onDragEnd={handleDragEnd}>
                <div className="row mb-4">
                    <div className="col-md-6">
                        <h5>Доступные компоненты</h5>
                        <Droppable droppableId="availableComponents">
                            {(provided) => (
                                <Table ref={provided.innerRef} {...provided.droppableProps} striped bordered hover>
                                    <thead>
                                        <tr>
                                            <th>Фото</th>
                                            <th>Название</th>
                                            <th>Партия</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                    {availableComponents.map((component, index) => (
                                        <Draggable key={component?.id?.toString()} draggableId={component?.id?.toString()} index={index}>
                                            {(provided) => (
                                                <tr ref={provided.innerRef} {...provided.draggableProps} {...provided.dragHandleProps}>
                                                    <td>
                                                        <img 
                                                            src={buildMediaUrl(component?.photo)} 
                                                            alt={component?.name} 
                                                            style={{ width: '50px', cursor: 'pointer' }} 
                                                            onClick={() => handleShowModal(buildMediaUrl(component?.photo))}
                                                        />
                                                    </td>
                                                    <td>
                                                        {renderCellWithTooltip(component?.name)}
                                                    </td>
                                                    <td>
                                                        {renderCellWithTooltip(component?.batch_number || 'N/A')}
                                                    </td>
                                                </tr>
                                            )}
                                        </Draggable>
                                    ))}
                                    {provided.placeholder}
                                    </tbody>
                                </Table>
                            )}
                        </Droppable>
                    </div>

                    <div className="col-md-6">
                        <h5>Выбранные компоненты для клонирования</h5>
                        <Droppable droppableId="selectedComponents">
                            {(provided) => (
                                <Table ref={provided.innerRef} {...provided.droppableProps} striped bordered hover>
                                    <thead>
                                        <tr>
                                            <th>Фото</th>
                                            <th>Название</th>
                                            <th>Партия</th>
                                            <th>Место хранения</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                    {selectedComponents.map((component, index) => (
                                        <Draggable key={component?.id?.toString()} draggableId={component?.id?.toString()} index={index}>
                                            {(provided) => (
                                                <tr ref={provided.innerRef} {...provided.draggableProps} {...provided.dragHandleProps}>
                                                    <td>
                                                        <img 
                                                            src={buildMediaUrl(component?.photo)} 
                                                            alt={component?.name} 
                                                            style={{ width: '50px', cursor: 'pointer' }} 
                                                            onClick={() => handleShowModal(buildMediaUrl(component?.photo))}
                                                        />
                                                    </td>
                                                    <td>
                                                        {renderCellWithTooltip(component?.name)}
                                                    </td>
                                                    <td>
                                                        {renderCellWithTooltip(component?.batch_number)}
                                                    </td>
                                                    <td>
                                                        <Select
                                                            name="storage_location"
                                                            options={storages}
                                                            value={storages.find(stor => stor.value === component.storage_location)}
                                                            onChange={(option) => {
                                                                const updatedComponents = [...selectedComponents];
                                                                updatedComponents[index].storage_location = option ? option.value : null; // Добавляем проверку на null
                                                                setSelectedComponents(updatedComponents);
                                                            }}
                                                            styles={selectStyles}
                                                            classNamePrefix="warehouse-select"
                                                            components={{ MenuList: CustomMenuList }}
                                                            isSearchable
                                                            isClearable
                                                        />
                                                    </td>
                                                </tr>
                                            )}
                                        </Draggable>
                                    ))}
                                    {provided.placeholder}
                                    </tbody>
                                </Table>
                            )}
                        </Droppable>
                    </div>
                </div>
            </DragDropContext>

            <OverlayTrigger 
                placement="top" 
                overlay={<Tooltip id="tooltip-save">Сохранить клонированные компоненты</Tooltip>}
            >
            <Button variant="primary" onClick={handleSaveClonedComponents}>
                <i className="fas fa-save"></i>
            </Button>
            </OverlayTrigger>


            <Modal
                show={showModal}
                onHide={() => setShowModal(false)}
                centered
                className="warehouse-modal warehouse-media-modal"
                dialogClassName="warehouse-modal-dialog warehouse-modal-dialog--lg"
                contentClassName="warehouse-modal-content"
            >
                <Modal.Header closeButton>
                    <Modal.Title>Увеличенное изображение</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <img src={modalImage} alt="component" style={{ width: '100%' }} />
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" onClick={() => setShowModal(false)}>
                        Закрыть
                    </Button>
                </Modal.Footer>
            </Modal>

            <StorageCategoryModal
                show={showStorageCategoryModal}
                handleClose={() => setShowStorageCategoryModal(false)}
                onSubmit={() => setShowStorageCategoryModal(false)}
            />
        </div>
    );
}

export default ComponentCloneConstructorPage;
